/**
 * Generic cellular / HTTPS adapter — IOT-ADAPTER-LIBRARY-1.
 *
 * Tier: aggregator (protocol-level adapter; works with any cellular IoT device
 * posting telemetry over HTTPS — including CAT-M1, NB-IoT, 4G LTE-CAT-1
 * trackers from no-name OEMs that ship with a configurable HTTPS POST target)
 * Category: asset_tracker, fleet_telematics
 *
 * The third "Switzerland strategy" exemplar. The adapter terminates an inbound
 * webhook (`functions/iot/genericCellularWebhook`) per-tenant, applies the
 * tenant-configured field-map to translate the device JSON into canonical
 * NormalizedReading objects, and routes through HalService like any other
 * adapter. Vendor-agnostic by construction.
 *
 * TYPE-ONLY for this session: capability matrix declared; webhook handler,
 * field-map executor, and shared-secret HMAC verification deferred to a
 * dedicated integration session.
 *
 * Patent anchor: F-IOT-ADAPTER-001 (hardware-agnostic IoT integration).
 */

import { NotImplementedAdapter } from '../NotImplementedAdapter.js';

const GENERIC_CELLULAR_CAPABILITIES = {
  configKeys: {
    webhook_url: {
      pushable: false,
      readable: true,
      requiresActivation: true,
      description: 'Inbound HTTPS endpoint configured at the device side; provisioning-time only',
    },
    shared_secret: {
      pushable: false,
      readable: false,
      requiresActivation: true,
      description: 'HMAC-SHA256 shared secret for webhook authenticity; writeonly',
    },
    field_map_profile: {
      pushable: true,
      readable: true,
      requiresActivation: false,
      description: 'Tenant-configured JSON field-map identifier (key paths → SensorType)',
    },
    reporting_interval_sec: {
      pushable: true,
      readable: true,
      range: [10, 86400],
      requiresActivation: false,
      unit: 'seconds',
      description: 'Telemetry POST cadence (informational; device-side enforced)',
    },
    cellular_apn: {
      pushable: false,
      readable: true,
      requiresActivation: true,
      description: 'Cellular APN; set at SIM provisioning',
    },
  },
  telemetryTypes: [
    'gps_position', 'speed_kph', 'heading_deg', 'temperature_c', 'humidity_pct',
    'shock_g', 'tilt_deg', 'battery_voltage_v', 'battery_soc_pct',
    'cell_signal_dbm',
  ],
  alertingMode: 'cloud',
  otaSupported: false,
  attestationSupported: false,
  rollbackSupported: false,
  pushLatencyExpectedMs: 30_000,
};

export class GenericCellularAdapter extends NotImplementedAdapter {
  constructor(device) {
    super(device, 'generic_cellular', GENERIC_CELLULAR_CAPABILITIES);
  }
}

export function createGenericCellularAdapterFactory() {
  return (device) => new GenericCellularAdapter(device);
}
